import { useState } from "react";

import type { BudgetPrompt } from "../types";
import styles from "./BudgetSlider.module.css";

const fmt = (n: number) =>
  n >= 100_000
    ? `₹${(n / 100_000).toFixed(2).replace(/\.?0+$/, "")}L`
    : `₹${Math.round(n / 1000)}k`;

// The slider's own two labels, in the language the reply was written in —
// the same reason the card's labels are localised. Kept here rather than
// imported so this component stays independent of the card.
const SLIDER_LABELS: Record<string, { budget: string; show: string }> = {
  en: { budget: "YOUR BUDGET", show: "SHOW DELL LAPTOPS FOR" },
  hi: { budget: "आपका बजट", show: "डेल लैपटॉप दिखाओ" },
};

interface Props {
  prompt: BudgetPrompt;
  disabled?: boolean;
  onPick: (valueInr: number) => void;
  /** Language of the reply, so the two labels match the words above. */
  language?: string;
}

export function BudgetSlider({ prompt, disabled, onPick, language }: Props) {
  const labels =
    SLIDER_LABELS[(language || "en").toLowerCase()] || SLIDER_LABELS.en;
  const { min_inr: min, max_inr: max, step_inr: step, suggested_inr } = prompt;
  const [value, setValue] = useState(
    Math.min(max, Math.max(min, suggested_inr || Math.round((min + max) / 2))),
  );

  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const fill =
    `linear-gradient(90deg, var(--aero-accent) 0%, ` +
    `var(--aero-accent-2, var(--aero-accent)) ${pct}%, ` +
    `rgba(255,255,255,0.12) ${pct}%, rgba(255,255,255,0.12) 100%)`;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.tag}>{labels.budget}</span>
        <span className={styles.value}>{fmt(value)}</span>
      </div>

      <input
        className={styles.range}
        type="range"
        min={min}
        max={max}
        step={step || 5000}
        value={value}
        disabled={disabled}
        aria-label="Budget in rupees"
        onChange={(e) => setValue(Number(e.target.value))}
        style={{ background: fill }}
      />

      <div className={styles.scale}>
        <span>{fmt(min)}</span>
        <span>{fmt(max)}</span>
      </div>

      <button
        type="button"
        className={styles.cta}
        disabled={disabled}
        onClick={() => onPick(value)}
      >
        {labels.show} {fmt(value)}
      </button>
    </div>
  );
}
